import {
  FavoriteBorder as MatchesIcon,
  InsertDriveFile as FilesIcon,
  MailOutline,
  OpenInNewOutlined,
  GroupAddOutlined,
  EventOutlined,
  CampaignOutlined,
  AccountBoxOutlined,
  Diversity3Outlined,
  PhotoLibraryOutlined,
  HomeOutlined,
  AttachMoneyOutlined,
  ChecklistOutlined,
  ImageOutlined,
  SchoolOutlined,
  ArticleOutlined,
  SettingsOutlined,
  LockOutlined as LockIcon,
  BookmarkBorderOutlined,
  PsychologyOutlined,
  ConstructionOutlined,
} from "@mui/icons-material";
import { PlanKey } from "../../../../pages/registration/homeowner-plan-selection-fieldset";
import { HOMEOWNER_FEATURES } from "../../../../constants/features";
import { FeatureFlagKey } from "../../../../contexts/FeatureFlagsContext";
import { OnboardingAccessControl } from "../../../../hooks/useDesignerOnboardingAccess";

export interface SidebarFeatureAccessProps {
  isDesigner: boolean;
  userPlan: PlanKey;
  isDesignerApproved: boolean;
  featureFlags: {
    isFeatureEnabled: (key: FeatureFlagKey, accountType?: string) => boolean;
  };
  onboardingAccess?: OnboardingAccessControl; // For designers
  homeownerOnboardingAccess?: {
    matchingProfileUnlocked: boolean;
    bioUnlocked: boolean;
  }; // For homeowners
  canAccessFeature?: (feature: string) => boolean; // Legacy tier-based access
  matchesBadgeCount?: number;
}

export const getSidebarConfigWithFlags = ({
  isDesigner,
  userPlan,
  isDesignerApproved,
  featureFlags,
  onboardingAccess,
  homeownerOnboardingAccess,
  canAccessFeature,
  matchesBadgeCount
}: SidebarFeatureAccessProps) => {
  
  // Helper to check if a feature is accessible
  const isFeatureAccessible = (flagKey: FeatureFlagKey): boolean => {
    // Check feature flag first
    const accountType = isDesigner ? 'Designer' : 'Homeowner';
    const flagEnabled = featureFlags.isFeatureEnabled(flagKey, accountType);
    
    if (!flagEnabled) return false;
    
    // For designers, check onboarding access
    if (isDesigner && onboardingAccess) {
      return onboardingAccess.canAccessFeature(flagKey);
    }
    
    return true;
  };

  // Legacy tier-based access check for homeowner features
  const hasLegacyFeatureAccess = (feature: string): boolean => {
    if (canAccessFeature) {
      return canAccessFeature(feature);
    }
    if (isDesigner) return true;
    return userPlan !== 'basic';
  };

  // Calculate locks for homeowner features
  // For homeowners, Matching Profile unlocks after course completion (not just plan-based)
  // Priority: If onboarding access exists, use it. Otherwise, fall back to legacy feature access.
  const isSmartMatchingLocked = !isDesigner && (
    userPlan !== "GuildPlus" ||
    (homeownerOnboardingAccess
      ? !homeownerOnboardingAccess.matchingProfileUnlocked
      : !hasLegacyFeatureAccess(HOMEOWNER_FEATURES.SMART_GUILD_MATCHING))
  );
  
  // Check if user has paid plan (Guild or GuildPlus)
  const hasPaidHomeownerPlan = !isDesigner && (userPlan === 'Guild' || userPlan === 'GuildPlus');
  
  // Homeowner feature access
  // Guild Guides is tier-gated only (Guild/GuildPlus); other items may still use feature flags.
  const isProjectGalleryEnabled = !isDesigner && isFeatureAccessible('project-gallery') && hasPaidHomeownerPlan;
  const isIdeaBooksEnabled = !isDesigner && hasPaidHomeownerPlan;
  const isGuildGuidesEnabled = !isDesigner && hasPaidHomeownerPlan;
  const isCoursesEnabled = !isDesigner && isFeatureAccessible('courses') && hasPaidHomeownerPlan;
  
  // Lock features based on enabled status above
  const isProjectGalleryLocked = !isDesigner && !isProjectGalleryEnabled;
  const isIdeaBooksLocked = !isDesigner && !isIdeaBooksEnabled;
  const isGuildGuidesLocked = !isDesigner && !isGuildGuidesEnabled;
  const isCoursesLocked = !isDesigner && !isCoursesEnabled;
  
  // Legacy checks for other education features (Events)
  const isEducationLocked = !isDesigner && !hasLegacyFeatureAccess(HOMEOWNER_FEATURES.EDUCATION);
  
  // For homeowners, Bio unlocks after matching profile is completed
  const isHomeownerProfileLocked = !isDesigner && (
    !hasPaidHomeownerPlan ||
    (homeownerOnboardingAccess ? !homeownerOnboardingAccess.bioUnlocked : true)
  );

  // Calculate locks for designer features based on onboarding
  // For designers: Matching Profile, Matches, and Messages should be locked until application is approved
  // They should be locked for: ToBeCompleted, CompleteApplication, UnderReview statuses
  // They should be unlocked for: Approved, MembershipActive statuses
  const isMatchingProfileLocked = isDesigner && !(onboardingAccess?.isMatchingProfileUnlocked ?? isDesignerApproved);
  const isBioLocked = isDesigner && !(onboardingAccess?.isBioUnlocked ?? isDesignerApproved);
  const isDesignerFilesLocked = isDesigner && !(onboardingAccess?.isDesignerFilesUnlocked ?? isDesignerApproved);
  // Matches and Messages should also be locked for designers until approved (same as Matching Profile)
  const isMatchesLocked = isDesigner && isMatchingProfileLocked;
  const isMessagesLocked = isDesigner && isMatchingProfileLocked;

  return [
    {
      name: "Your Hub",
      path: "/your-hub",
      icon: <HomeOutlined sx={{ color: "#fff" }} />,
    },
    {
      name: "Smart Guild Matching",
      subItems: [
        { 
          name: "Matches", 
          path: "/matches", 
          icon: isMatchesLocked || isMatchingProfileLocked || isSmartMatchingLocked ? <LockIcon sx={{ color: "rgba(255, 255, 255, 0.5)" }} /> : <MatchesIcon sx={{ color: "#fff" }} />,
          badge: matchesBadgeCount ?? 0,
          locked: isMatchesLocked || isMatchingProfileLocked || isSmartMatchingLocked,
        },
        {
          name: "Messages",
          path: "/messages",
          icon: isMessagesLocked || isMatchingProfileLocked || isSmartMatchingLocked ? <LockIcon sx={{ color: "rgba(255, 255, 255, 0.5)" }} /> : <MailOutline sx={{ color: "#fff" }} />,
          locked: isMessagesLocked || isMatchingProfileLocked || isSmartMatchingLocked,
        },
      ]
    },
    ...(isDesigner
      ? [
          // Show Collaborate section only if any features are enabled
          ...((isFeatureAccessible('our-partners') || isFeatureAccessible('refer-designer') || isFeatureAccessible('events'))
            ? [{
                name: "Collaborate",
                subItems: [
                  ...(isFeatureAccessible('our-partners') ? [{
                    name: "Our Partners", 
                    path: "/partners", 
                    icon: <OpenInNewOutlined sx={{ color: "#fff" }} /> 
                  }] : []),
                  ...(isFeatureAccessible('refer-designer') ? [{
                    name: "Refer a Designer", 
                    path: "/refer",
                    icon: <GroupAddOutlined sx={{ color: "#fff" }} />,
                  }] : []),
                  ...(isFeatureAccessible('events') ? [{
                    name: "Events", 
                    path: "/events", 
                    icon: <EventOutlined sx={{ color: "#fff" }} />
                  }] : []),
                ],
              }]
            : []),
          // Show Marketing section only if any features are enabled
          ...((isFeatureAccessible('campaigns') || isFeatureAccessible('guild-seal-assets') || isFeatureAccessible('marketing'))
            ? [{
                name: "Marketing",
                subItems: [
                  ...(isFeatureAccessible('campaigns') ? [{
                    name: "Campaigns", 
                    path: "/campaigns",
                    icon: <CampaignOutlined sx={{ color: "#fff" }} />,
                  }] : []),
                  ...(isFeatureAccessible('guild-seal-assets') ? [{
                    name: "Guild Seal Assets", 
                    path: "/assets",
                    icon: <FilesIcon sx={{ color: "#fff" }} />,
                  }] : []),
                ],
              }]
            : []),
          // Show Guild IQ section if enabled
          ...(isFeatureAccessible('guild-iq') ? [{
            name: "Guild IQ",
            path: "/guild-iq",
            icon: <PsychologyOutlined sx={{ color: "#fff" }} />,
          }] : []),
        ]
      : []),
    {
      name: "Manage My Profile",
      subItems: [
        ...(isDesigner ? [
          { 
            name: "Matching Profile", 
            path: "/my-matching-profile", 
            icon: isMatchingProfileLocked ? <LockIcon sx={{ color: "rgba(255, 255, 255, 0.5)" }} /> : <AccountBoxOutlined sx={{ color: "#fff" }} />,
            locked: isMatchingProfileLocked,
          },
          { 
            name: "My Bio", 
            path: "/my-bio", 
            icon: isBioLocked ? <LockIcon sx={{ color: "rgba(255, 255, 255, 0.5)" }} /> : <Diversity3Outlined sx={{ color: "#fff" }} />,
            locked: isBioLocked,
          },
        ] : [
          { 
            name: "Matching Profile", 
            path: "/my-matching-profile", 
            icon: isSmartMatchingLocked ? <LockIcon sx={{ color: "rgba(255, 255, 255, 0.5)" }} /> : <AccountBoxOutlined sx={{ color: "#fff" }} />,
            locked: isSmartMatchingLocked,
          },
          { 
            name: "Projects", 
            path: "/my-projects", 
            icon: isSmartMatchingLocked ? <LockIcon sx={{ color: "rgba(255, 255, 255, 0.5)" }} /> : <ConstructionOutlined sx={{ color: "#fff" }} />,
            locked: isSmartMatchingLocked,
          },
          { 
            name: "My Profile", 
            path: "/homeowner-profile", 
            icon: isHomeownerProfileLocked ? <LockIcon sx={{ color: "rgba(255, 255, 255, 0.5)" }} /> : <Diversity3Outlined sx={{ color: "#fff" }} />,
            locked: isHomeownerProfileLocked,
          },
        ]),
        ...(isDesigner ? [
          // Always show Content section for designers during onboarding
          // Designer Files will be locked/unlocked based on onboarding progress
          ...(isFeatureAccessible('designer-files') || isFeatureAccessible('designer-confessions') || isDesignerApproved
            ? [{
                name: "Content",
                icon: <PhotoLibraryOutlined sx={{ color: "#fff" }} />,
                subItems: [
                  ...(isFeatureAccessible('designer-files') || isDesignerApproved ? [{
                    name: "Designer Files", 
                    path: "/my-design-files", 
                    icon: isDesignerFilesLocked ? <LockIcon sx={{ color: "rgba(255, 255, 255, 0.5)" }} /> : <></>,
                    locked: isDesignerFilesLocked
                  }] : []),
                  ...(isFeatureAccessible('designer-confessions') ? [{
                    name: "Designer Confessions", 
                    path: "/designer-confessions",
                    icon: <></>,
                  }] : []),
                ]
              }]
            : []),
          ...(isFeatureAccessible('project-reporting') ? [{
            name: "Project Reporting",
            path: "/project-reporting",
            icon: <ChecklistOutlined sx={{ color: "#fff" }} />,
          }] : []),
          ...(isFeatureAccessible('quick-links') ? [{
            name: "Quick Links",
            path: "/quick-links",
            icon: <OpenInNewOutlined sx={{ color: "#fff" }} />,
          }] : []),
        ] : []),
      ],
    },
    ...(
      !isDesigner ? [
        {
          name: "Inspiration",
          subItems: [
            ...(hasPaidHomeownerPlan
              ? [
                  {
                    name: "Designer Files",
                    path: "/homeowner-designer-files",
                    icon: <FilesIcon sx={{ color: "#fff" }} />,
                    locked: false,
                  },
                ]
              : []),
            { 
              name: "My Idea Books", 
              path: "/my-idea-books", 
              icon: isIdeaBooksLocked ? <LockIcon sx={{ color: "rgba(255, 255, 255, 0.5)" }} /> : <BookmarkBorderOutlined sx={{ color: "#fff" }} />,
              locked: isIdeaBooksLocked,
            },
          ]
        },
        {
          name: "Education",
          subItems: [
            {
              name: "Guild Guides",
              path: "/guides",
              icon: isGuildGuidesLocked ? <LockIcon sx={{ color: "rgba(255, 255, 255, 0.5)" }} /> : <ArticleOutlined sx={{ color: "#fff" }} />,
              locked: isGuildGuidesLocked,
            },
            {
              name: "Courses",
              path: "/courses",
              icon: isCoursesLocked ? <LockIcon sx={{ color: "rgba(255, 255, 255, 0.5)" }} /> : <SchoolOutlined sx={{ color: "#fff" }} />,
              locked: isCoursesLocked,
            },
            {
              name: "Events",
              path: "/events",
              icon: isEducationLocked ? <LockIcon sx={{ color: "rgba(255, 255, 255, 0.5)" }} /> : <EventOutlined sx={{ color: "#fff" }} />,
              locked: isEducationLocked,
            },
          ],
        },
      ] : []
    ),
    {
      name: "Utilities",
      subItems: [
        { name: "My Account", path: "/my-account", icon: <SettingsOutlined sx={{ color: "#fff" }} /> },
      ],
    },
    ...(isDesigner
      ? [
          {
            name: "Partner Offers",
            subItems: [
              {
                name: "Financing",
                path: "https://homedesignguild.com/financing",
                external: true,
                icon: <AttachMoneyOutlined sx={{ color: "#fff" }} />,
              },
            ],
          },
        ]
      : []),
  ];
};
